import { Injectable, signal } from '@angular/core';

/** Delay before list filtering runs after typing pauses (input stays immediate via `query`). */
const FILTER_DEBOUNCE_MS = 250;

@Injectable({ providedIn: 'root' })
export class SearchService {
  readonly query = signal<string>('');
  /** Debounced mirror of `query` for expensive list filtering; stays in sync when cleared or synced. */
  readonly filterQuery = signal<string>('');

  private debounceTimer: ReturnType<typeof setTimeout> | undefined;

  setQuery(value: string | null | undefined = ''): void {
    const safeValue = value ?? '';
    this.query.set(safeValue);
    if (this.debounceTimer !== undefined) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = undefined;
    }
    this.debounceTimer = globalThis.setTimeout(() => {
      this.debounceTimer = undefined;
      this.filterQuery.set(safeValue);
    }, FILTER_DEBOUNCE_MS);
  }

  /**
   * Sets `filterQuery` to current `query` immediately (no pending debounce).
   * Use after restoring list state so filtering matches the visible search text without delay.
   */
  syncFilterQueryWithQuery(): void {
    if (this.debounceTimer !== undefined) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = undefined;
    }
    this.filterQuery.set(this.query());
  }

  clear(): void {
    if (this.debounceTimer !== undefined) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = undefined;
    }
    this.query.set('');
    this.filterQuery.set('');
  }

  /** Clears pending debounce timer only (e.g. on component destroy). Does not change query values. */
  cancelDebouncedFilterUpdate(): void {
    if (this.debounceTimer !== undefined) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = undefined;
    }
  }
}
